import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useCaseStudies = () => {
  const queryClient = useQueryClient();

  const { data: caseStudies, isLoading } = useQuery({
    queryKey: ['case_studies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_studies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const createCaseStudy = useMutation({
    mutationFn: async (newCaseStudy: any) => {
      const { data, error } = await supabase
        .from('case_studies')
        .insert([newCaseStudy])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case_studies'] });
      toast.success('Case study created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create case study');
    },
  });

  const updateCaseStudy = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { data, error } = await supabase
        .from('case_studies')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case_studies'] });
      toast.success('Case study updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update case study');
    },
  });

  const deleteCaseStudy = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('case_studies')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case_studies'] });
      toast.success('Case study deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete case study');
    },
  });

  return {
    caseStudies,
    isLoading,
    createCaseStudy,
    updateCaseStudy,
    deleteCaseStudy,
  };
};
